<template>
  <div class="candidate-list-card card mt-4">
    <div class="table-controls m-3">
      <div class="controls-wrap d-flex">
        <input
          :disabled="isLoading"
          type="search"
          id="integration-search"
          class="form-control mr-2"
          aria-label="SearchGrid"
          aria-describedby="searchGrid"
          placeholder="Search (name and email)"
          v-model="searchWord"
          @input="checkSearch"
          @keyup.enter="debouncedSearch"
        />

        <button
          style="align-self: flex-start"
          class="btn btn-outline mr-2"
          type="button"
          @click="addFilter((input = {}), filters)"
        >
          <i class="fas fa-plus-circle"></i>
        </button>

        <div class="d-flex align-items-center">
          <!-- Employer -->
          <div class="empl" style="align-self: flex-start">
            <v-select
              v-model="listOfSelectedEmployers"
              @input="handleSearchFilters($event)"
              :options="getEmployersDropdown"
              placeholder="All Employers"
              inputId="employer-select"
              label="name"
              multiple
              :reduce="employer => employer"
              :clearable="false"
            >
              <!-- <template #option="{ name, city }"> {{ name }} - {{ city }} </template> -->
            </v-select>
          </div>
          <!-- Sources -->
          <div class="empl" style="align-self: flex-start">
            <v-select
              v-model="listOfSelectedSources"
              @input="handleSearchFilters($event)"
              :options="getSourceList"
              placeholder="All Sources"
              inputId="sources-select"
              label="source_name"
              multiple
              :reduce="source => source"
              :clearable="false"
            >
              <template #option="{ source_name, description }">
                {{ description }} ({{ source_name }})
              </template>
              <template #selected-option="{ source_name }">
                {{ source_name }}
              </template>
            </v-select>
          </div>

          <popper
            class="popperr"
            style="align-self: flex-start; margin-top: 4px"
            trigger="hover"
            :options="{
              placement: 'top',
              modifiers: { offset: { offset: '0,10px' } },
            }"
          >
            <div
              class="popper opted-out custom-control custom-checkbox no-mobile ml-auto button-show"
            >
              {{
                !freelancers
                  ? 'Show only Freelancing Network members'
                  : 'Remove Freelancing Network filter'
              }}
            </div>
            <div style="align-self: flex-start" slot="reference">
              <span
                class="fa-stack"
                style="text-align:center"
                @click="
                  freelancers = !freelancers;
                  handleSearchFilters($event);
                "
              >
                <i
                  style="font-size:23px; margin-top:3px"
                  :class="[freelancers ? 'is-clicked' : '', 'fas fa-dove', 'popover-show']"
                ></i>
              </span>
            </div>
          </popper>
          <popper
            class="popperr"
            style="align-self: flex-start; margin-top: 4px"
            trigger="hover"
            :options="{
              placement: 'top',
              modifiers: { offset: { offset: '0,10px' } },
            }"
          >
            <div
              class="popper opted-out custom-control custom-checkbox no-mobile ml-auto button-show"
            >
              {{ !isOptedOut ? 'Include opted-out candidates' : 'Exclude opted-out candidates' }}
            </div>
            <div style="align-self: flex-start" slot="reference">
              <span
                class="fa-stack"
                @click="
                  isOptedOut = !isOptedOut;
                  handleSearchFilters($event);
                "
              >
                <i
                  :class="[
                    isOptedOut ? 'is-clicked' : '',
                    'fas fa-ban',
                    'fa-stack-2x',
                    'popover-show',
                  ]"
                ></i>
                <i
                  :class="[
                    isOptedOut ? 'is-clicked' : '',
                    'fas fa-user',
                    'fa-stack-1x',
                    'fa-inverse',
                  ]"
                ></i>
              </span>
            </div>
          </popper>
          <!-- Add btn -->
          <button
            style="align-self: flex-start"
            type="button"
            class="ml-3 btn btn-success text-nowrap desktop-only"
            @click="showModal = true"
          >
            Add Candidate
            <portal to="add-candidate-modal">
              <CustomMainModal @close="closeModal" v-show="showModal">
                <template v-slot:header>
                  <h5>Add Candidate</h5>
                </template>

                <AddCandidateBtnModal v-if="showModal" @close="closeModal" />
              </CustomMainModal>
            </portal>
          </button>
        </div>
        <portal-target name="add-candidate-modal"></portal-target>
      </div>

      <!-- FILTERS -->
      <section class="filter-items">
        <div class="row mt-3">
          <div
            v-for="(input, index) in filters"
            :key="`phoneInput-${index}`"
            class="col-md-8 filter"
          >
            <div class="d-flex">
              <select
                @change="selectedFilterType"
                name="select-filter"
                class="form-control filter-select"
                v-model="selectedFilterName[index]"
              >
                <option value="location">Location</option>
                <option value="distance">City (distance)</option>
                <option value="employment">Employment type</option>
                <option value="competences">Competencies</option>
                <option value="relocation">Relocation</option>
                <option value="languages">Languages</option>
                <option value="hired">Hired</option>
              </select>

              <v-select
                v-if="selectedFilterName[index] == 'location'"
                @input="handleVSelectFields($event, selectedFilterName)"
                @search="fetchOptions"
                v-model="input['location']"
                class="mr-1 w-100"
                label="name"
                placeholder="Location"
                aria-label="location"
                aria-describedby="location-input"
                :options="[...locationsOptions]"
                :reduce="location => location"
                :clearable="false"
                multiple
              >
                <template v-slot:no-options>
                  <div style="opacity: 0.5">Please type at least 3 characters</div>
                </template>
              </v-select>

              <v-select
                v-if="selectedFilterName[index] == 'distance'"
                @change="handleVSelectFields($event, selectedFilterName)"
                @search="fetchOptionsForCities"
                v-model="input['city']"
                class="mr-1 w-100"
                inputId="city-distance"
                label="name"
                placeholder="City name"
                aria-label="city-distance"
                aria-describedby="city-distance-input"
                :options="citiesByNameOptions"
                :reduce="city => city"
                :clearable="false"
                autocorrect="off"
                spellcheck="false"
                autocomplete="off"
              >
                <template v-slot:no-options>
                  <div style="opacity: 0.5">Please type at least 3 characters</div>
                </template>
              </v-select>

              <input
                v-if="selectedFilterName[index] == 'distance'"
                v-model="input['distance']"
                @input="toggleLabel($event)"
                type="text"
                id="distance"
                name="distance"
                style="max-width: 145px; min-width: 145px"
                class="form-control mr-1"
                aria-describedby="CityDistanceHelp"
                placeholder="Distance (km)"
              />
              <label
                v-if="selectedFilterName[index] == 'distance' && showLabel"
                class="custom-label"
                for="distance"
                >km</label
              >

              <select
                v-if="selectedFilterName[index] == 'employment'"
                @input="handleVSelectFields($event, selectedFilterName)"
                class="form-control w-100"
                name="employment"
                v-model="input['employment']"
              >
                <option value="1">Employment</option>
                <option value="2">Self-employment</option>
                <!-- <option value="3">Employment / Self-Employment</option> -->
              </select>

              <v-select
                v-if="selectedFilterName[index] == 'competences'"
                @input="handleVSelectFields($event, selectedFilterName)"
                v-model="input['competences']"
                class="w-100 mr-1"
                placeholder="Competencies"
                label="name"
                :options="[...getCompetencesFilterList]"
                :reduce="competence => competence.id"
                :clearable="false"
                multiple
              >
                <template v-slot:no-options>
                  <div style="opacity: 0.5">Sorry, no matching competence</div>
                </template>
              </v-select>

              <select
                v-if="selectedFilterName[index] == 'serviceLevel'"
                class="form-control mr-0"
                v-model="input['serviceLevel']"
                @input="handleVSelectFields($event, selectedFilterName)"
                name="service-level"
              >
                <option>Basic</option>
                <option>Sourcing</option>
                <option>Full-service</option>
              </select>

              <select
                v-if="selectedFilterName[index] == 'relocation'"
                @input="handleVSelectFields($event, selectedFilterName)"
                class="form-control"
                v-model="input['relocation']"
                name="relocation"
              >
                <option :value="false">No</option>
                <option :value="true">Yes</option>
              </select>

              <v-select
                v-if="selectedFilterName[index] == 'languages'"
                @input="handleVSelectFields($event, selectedFilterName)"
                v-model="input['languages']"
                class="w-100 mr-1"
                placeholder="Languages"
                label="name"
                :options="[...getLanguagesList]"
                :reduce="language => language.id"
                :clearable="false"
                multiple
              >
                <template v-slot:no-options>
                  <div style="opacity: 0.5">Sorry, no matching language</div>
                </template>
              </v-select>

              <select
                v-if="selectedFilterName[index] == 'hired'"
                @input="handleVSelectFields($event, selectedFilterName)"
                class="form-control"
                v-model="input['hired']"
                name="hired"
              >
                <option :value="false">No</option>
                <option :value="true">Yes</option>
              </select>

              <button
                class="btn btn-outline-remove"
                type="button"
                @click="removeSingleFilter(index, filters)"
              >
                <i class="fas fa-times-circle"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="row" v-show="filters.length > 0">
          <div class="col-md-8 d-flex filter">
            <div class="ml-auto">
              <button
                type="button"
                class="btn btn-success text-nowrap text-white mt-2 mr-1"
                @click="applyFilters"
              >
                Apply Filters
              </button>
              <button
                type="button"
                class="btn btn-danger text-nowrap text-white mt-2 mr-1"
                @click="removeAllFilters"
              >
                Remove Filters
              </button>
              <button
                class="btn btn-outline mt-2"
                type="button"
                @click="addFilter((input = {}), filters)"
              >
                <i class="fas fa-plus-circle"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- END FILTERS -->
    </div>

    <div class="table-responsive" :class="getCandidates.length === 0 ? 'overflow' : ''">
      <Spinner :isLoading="isLoading" />
      <table class="table table-striped custom-table desktop-only-table">
        <thead class="bg-dark">
          <tr>
            <th nowrap scope="col">NAME</th>
            <th nowrap scope="col">LOCATION</th>
            <th nowrap scope="col">SOURCE</th>
            <th nowrap scope="col" style="text-align: center">&nbsp;</th>
            <th nowrap scope="col" style="width: 100px">UPDATED</th>
            <th nowrap scope="col" style="width: 300px">Competencies</th>
            <th nowrap scope="col">RECENT STAGE</th>
            <th nowrap scope="col">RECENT JOB</th>
            <th nowrap scope="col">RECENT COMPANY</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="candidate in getCandidates" :key="candidate.id">
            <td id="candidate-name" class="candidate-name text-truncate" nowrap>
              <router-link
                v-if="candidate.status == 102"
                :to="{ name: 'candidate_id', params: { candidate_id: candidate.id } }"
              >
                <del>{{ candidate.first_name }} {{ candidate.last_name }}</del>
              </router-link>
              <div v-else slot="reference">
                <router-link :to="{ name: 'candidate_id', params: { candidate_id: candidate.id } }">
                  {{ candidate.first_name }} {{ candidate.last_name }}
                </router-link>
              </div>
            </td>
            <td id="candidate-location" nowrap>
              {{ getLocation(candidate) }}
            </td>
            <td id="candidate-source" nowrap>
              <div
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                :title="candidate.source_data.description"
              >
                {{ candidate.source_data.source_name }}
              </div>
            </td>
            <!-- MODEL -->
            <td style="right: 0" id="candidate-employment">
              <div class="employment-type d-flex align-items-center justify-content-center">
                <svgHelper
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="`Relocation: ${candidate.relocation ? 'Yes' : 'No'}`"
                  style="margin-right: 10px"
                  slot="reference"
                  :name="candidate.relocation ? 'relocate' : 'relocateFalse'"
                ></svgHelper>
                <!-- getEmploymentType from mixin! -->
                <svgHelper
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="`${getEmploymentType(candidate.employment).name}`"
                  slot="reference"
                  :name="getEmploymentType(candidate.employment).name"
                ></svgHelper>
              </div>
            </td>
            <!-- END MODEL -->

            <td id="candidate-updated" class="popover-show" nowrap>
              <popper
                trigger="hover"
                :options="{
                  placement: 'top',
                  modifiers: { offset: { offset: '-60,10px' } },
                }"
              >
                <div class="popper">
                  {{
                    candidate.updated_at
                      | formatDate({
                        format: getUserProfile.format_date,
                        timezone: getUserProfile.timezone,
                        hasHourAndMinutes: true,
                      })
                  }}
                </div>
                <div slot="reference">
                  {{
                    candidate.updated_at
                      | formatDate({
                        format: getUserProfile.format_date,
                        timezone: getUserProfile.timezone,
                        hasHourAndMinutes: false,
                      })
                  }}
                </div>
              </popper>
            </td>

            <td class="candidate-competences" id="candidate-competences">
              <div class="tech-icons d-flex flex-nowrap align-items-center">
                <div
                  class="text-center icon-container d-flex"
                  v-for="(competence, $index) in getCompetences(candidate.competences)"
                  :key="$index"
                >
                  <IconMapper :competence="competence" />
                </div>
                <i v-if="candidate.competences.length > 4" class="fas fa-ellipsis-h ml-2"></i>
                <div class="dropdown">
                  <button
                    v-show="candidate.competences.length > 4"
                    type="button"
                    class="btn btn-danger dropdown-toggle"
                    id="competencesDropDown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  ></button>

                  <ul class="list-inline dropdown-menu w-100" aria-labelledby="competencesDropDown">
                    <li
                      v-for="(c, i) in getRestCompetences(candidate.competences)"
                      :key="i"
                      class="list-inline-item"
                    >
                      <IconMapper :competence="c" />
                    </li>
                  </ul>
                </div>
              </div>
            </td>
            <td id="stage">
              <div class="d-flex align-items-center">
                <span
                  :class="[
                    getStageColorClass(candidate['recent-stage'].stage),
                    'flex-shrink-0',
                    'stage-size',
                    'rounded-circle',
                    'mr-2',
                  ]"
                ></span>
                <span
                  :class="[
                    getStageMixin(candidate['recent-stage'].stage).title ? 'text-nowrap' : 'mr-0',
                    'stage-text',
                  ]"
                  >{{ getStageMixin(candidate['recent-stage'].stage).title }}
                </span>
              </div>
            </td>

            <td id="candidate-recent-job">
              <router-link
                v-if="candidate['recent-stage']['job_id']"
                class="text-nowrap"
                :to="{
                  name: 'job_uuid',
                  params: { job_uuid: candidate['recent-stage']['job_id'] },
                }"
                >{{ candidate['recent-stage'].job }}</router-link
              >
            </td>
            <td id="candidate-recent-company">
              <span
                style="color: #0088cc"
                class="text-nowrap"
                v-if="candidate['recent-stage']['employer_id']"
              >
                <router-link
                  :to="{
                    name: 'employer_id',
                    params: {
                      employer_id: candidate['recent-stage']['employer_id'],
                      employer_name: candidate['recent-stage'].employer,
                    },
                  }"
                  >{{ candidate['recent-stage'].employer }}</router-link
                >
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ErrorList
      class="no-data-message"
      v-if="getCandidates.length == 0"
      name="noCandidates"
      :phrase="searchText"
    />

    <div class="d-flex ml-auto mt-auto bottom-controls">
      <div class="desktop-only ml-auto" id="pagination">
        <!-- Pagination -->
        <Pagination
          :currentPage="getCurrentPage"
          :totalPages="getTotalPages"
          @nextPage="nextPage"
          @prevPage="previousPage"
          @selectPageByNumber="setPageByNumber"
          @firstPage="setToFirstPage"
          @lastPage="setToLastPage"
          @keyup="keymonitor"
        ></Pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';

import Pagination from '@/components/Navigations/Pagination';
import CustomMainModal from '@/components/Modals/CustomMainModal';
import Search from '@/components/common/Search/Search';
import AddCandidateBtnModal from './AddCandidateBtnModal';

import IconMapper from '../../common/iconMapper';
import { stagesMixin } from '../../../mixins/stagesMixin';
import { statusMixin } from '../../../mixins/statusMixin';
import { employmentMixin } from '../../../mixins/employmentMixin';
import { displayResultsBasedOnScreenHeightMixin } from '../../../mixins/displayResultsBasedOnScreenHeightMixin';

import { errorService } from '../../../services/errorService';
import ErrorList from '@/components/common/errorsList';
import { localStorageHelper } from '@/utils/helpers';
import titleMixin from '../../../mixins/titleMixin';
import debounce from 'lodash/debounce';

export default {
  components: {
    Search,
    ErrorList,
    IconMapper,
    Pagination,
    CustomMainModal,
    AddCandidateBtnModal,
  },
  mixins: [
    stagesMixin,
    statusMixin,
    employmentMixin,
    displayResultsBasedOnScreenHeightMixin,
    titleMixin,
  ],
  title() {
    return 'Candidates | Jobshark';
  },
  /*   description() {
    return 'Page description goes here.';
  }, */
  canonicalLink() {
    return 'https://jobshark.eu/admin/candidates';
  },
  data: () => ({
    showLabel: false,
    locationsOptions: [],
    citiesByNameOptions: [],
    selectedFilterName: [],
    filters: [],
    listOfFilters: [],
    listOfSelectedEmployers: [],
    selectedFiltersList: [],
    employersSourcesOptions: [],
    listOfSelectedSources: [],
    searchText: '',
    isLoading: false,
    isOptedOut: false,
    freelancers: false,
    toggleFilter: false,
    showModal: false,
    noResults: false,
    currentPage: 1,
    pageCount: 10,
    infiniteScrollList: [],
    searchWord: '',
  }),
  methods: {
    ...mapActions([
      'retrieveCandidates',
      'retrieveEmployersDropdown',
      'searchLocationByName',
      'retrieveSourcesList',
      'retrieveCompetencesList',
    ]),

    checkSearch() {
      if (this.searchWord === '') {
        this.debouncedSearch();
      }
    },

    selectedFilterType(e) {
      if (e.target.value == 'distance') {
        this.filters.forEach(el => {
          if (el && Object.keys(el).length === 0) {
            el['distance'] = 100;
            this.showLabel = true;
          }
        });
      }

      if (e.target.value == 'employment') {
        this.filters.forEach(el => {
          if (el && Object.keys(el).length === 0) {
            el['employment'] = '1';
          }
        });
      }

      if (e.target.value == 'relocation') {
        this.filters.forEach(el => {
          if (el && Object.keys(el).length === 0) {
            el['relocation'] = false;
          }
        });
      }

      if (e.target.value == 'hired') {
        this.filters.forEach(el => {
          if (el && Object.keys(el).length === 0) {
            el['hired'] = false;
          }
        });
      }
    },
    handleVSelectFields(/* inputValue, filterType */) {
      // console.log('handleVSelectFields: ', inputValue, '|', filterType);
    },
    fetchOptions(search, loading) {
      if (search.length < 3) {
        this.locationsOptions = [];
      }

      if (search.length >= 3) {
        loading(true);

        this.searchLocationByName(search)
          .then(response => {
            const citiesList = response.data.cities;
            const countriesList = response.data.countries;
            const concatedLists = [].concat(countriesList, citiesList);
            this.locationsOptions = concatedLists;
            loading(false);
          })
          .catch(err => {
            loading(false);
            this.$toasted.error(errorService(err), {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            });
          });
      }
    },
    fetchOptionsForCities(search, loading) {
      if (search.length > 1) {
        loading(true);
        this.searchLocationByName(search)
          .then(response => {
            this.citiesByNameOptions = response.data.cities;

            loading(false);
          })
          .catch(err => {
            loading(false);
            this.$toasted.error(errorService(err), {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            });
          });
      }
    },
    addFilter(_value, fieldType) {
      fieldType.push({});
      this.selectedFilterName.push('location');
    },
    toggleLabel(e) {
      e.target.value ? (this.showLabel = true) : (this.showLabel = false);
    },
    removeSingleFilter(index, fieldType) {
      fieldType.splice(index, 1);
      this.selectedFilterName.splice(index, 1);

      this.filters.length < 1 ? this.reloadDefaultList() : '';
    },
    removeAllFilters() {
      this.filters = [];
      this.selectedFilterName = [];

      this.reloadDefaultList();
    },
    applyFilters() {
      // Remove invalid entries
      this.selectedFilterName.forEach((filterType, index) => {
        if (filterType == 'distance') {
          Object.keys(this.filters[index] || {}).forEach(key =>
            this.selectedFilterName.includes(key),
          );
        } else {
          Object.keys(this.filters[index] || {}).forEach(
            key => this.selectedFilterName.includes(key) || delete this.filters[index][key],
          );
        }
      });

      this.filters.forEach(item => {
        if (item['location']) {
          let tempList = item.location.map(i => i.id);

          this.listOfFilters.push({ location: tempList });

          // Send to API

          // console.log('location: ', this.listOfFilters);
        }

        if (item['city']) {
          this.listOfFilters.push({
            distance: {
              city_id: item['city'].id,
              distance: item['distance'] || 1,
            },
          });
        }

        if (item['employment']) {
          this.listOfFilters.push({
            employment: Number(item.employment),
          });
        }

        if (item['competences']) {
          this.listOfFilters.push(item);

          /*  console.log('competences: ', this.listOfFilters); */
        }

        if (item['relocation'] == true || item['relocation'] == false) {
          this.listOfFilters.push({
            relocation: item.relocation,
          });

          /*    console.log('relocation: ', this.listOfFilters); */
        }

        if (item['languages']) {
          this.listOfFilters.push(item);

          /*  console.log('languages: ', this.listOfFilters); */
        }

        if (item['hired'] == true || item['hired'] == false) {
          /*   console.log('hired: ', item); */

          this.listOfFilters.push({
            hired: item.hired,
          });

          /*  console.log('hired: ', this.listOfFilters); */
        }
      });

      this.doSearchWithFilters(this.listOfFilters);

      //Reset List
      this.listOfFilters = [];
    },
    keymonitor(event) {
      // who caused it? "event.target.id"
      // console.log('keyup from id: ' + event.target.id);

      // what was pressed?
      let keyMessage = event.key || String.fromCharCode(event.keyCode);

      /* if (event.shiftKey) {
        keyMessage += 'Shift+';
      } */

      if (keyMessage == 'ArrowRight' && !this.showModal) {
        this.nextPage();
      }

      if (keyMessage == 'ArrowLeft' && !this.showModal) {
        this.previousPage();
      }
    },
    closeModal() {
      this.currentPage = 1;
      this.showModal = false;
    },
    reloadDefaultList() {
      this.isLoading = true;

      this.selectedFiltersList = [];
      this.currentPage = 1;

      this.retrieveCandidates({
        search_word: this.searchWord,
        page: this.currentPage,
        count: this.pageCount,
        optedOut: this.isOptedOut,
        freelancers: this.freelancers,
        employersList: this.listOfSelectedEmployers.map(item => item.id),
        sourcesList: this.listOfSelectedSources.map(item => item.id),
        filters: [],
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;

          if (err.response.status == 401) {
            localStorageHelper();

            this.$toasted.error('Your session is expired!', {
              theme: 'bubble',
              icon: 'fa-check-circle',
              position: 'bottom-right',
              duration: 5000,
            });

            this.$router.push({ name: 'home' });
            location.reload();
          }

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });

          this.isLoading = false;
        });
    },
    handleSearchFilters(/* filter */) {
      this.isLoading = true;

      // Reset current page to 1
      this.currentPage = 1;

      this.retrieveCandidates({
        search_word: this.searchWord,
        page: this.currentPage,
        count: this.pageCount,
        optedOut: this.isOptedOut,
        freelancers: this.freelancers,
        employersList: this.listOfSelectedEmployers.map(item => item.id),
        sourcesList: this.listOfSelectedSources.map(item => item.id),
        filters: this.selectedFiltersList,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    doSearchWithFilters(filters) {
      console.log(this.searchWord);
      this.isLoading = true;
      this.currentPage = 1;

      this.selectedFiltersList = filters;

      // console.log('filters: ', filters);

      this.retrieveCandidates({
        search_word: this.searchWord,
        page: this.currentPage,
        count: this.pageCount,
        optedOut: this.isOptedOut,
        freelancers: this.freelancers,
        employersList: this.listOfSelectedEmployers.map(item => item.id),
        sourcesList: this.listOfSelectedSources.map(item => item.id),
        filters: filters,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;
          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    doSearch(searchedText) {
      this.currentPage = 1;

      if (this.searchWord.length >= 2) {
        this.isLoading = true;

        this.retrieveCandidates({
          search_word: this.searchWord,
          page: this.currentPage,
          count: this.pageCount,
          optedOut: this.isOptedOut,
          freelancers: this.freelancers,
          employersList: this.listOfSelectedEmployers.map(item => item.id),
          sourcesList: this.listOfSelectedSources.map(item => item.id),
          filters: this.selectedFiltersList,
        })
          .then(() => {
            this.isLoading = false;
          })
          .catch(err => {
            this.isLoading = false;
            this.$toasted.error(errorService(err), {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            });
          });
      }

      if (this.searchWord === '') {
        this.isLoading = true;

        this.retrieveCandidates({
          search_word: '',
          page: this.currentPage,
          count: this.pageCount,
          optedOut: this.isOptedOut,
          freelancers: this.freelancers,
          employersList: this.listOfSelectedEmployers.map(item => item.id),
          sourcesList: this.listOfSelectedSources.map(item => item.id),
          filters: this.selectedFiltersList,
        })
          .then(() => {
            this.isLoading = false;
          })
          .catch(err => {
            this.isLoading = false;
            this.$toasted.error(errorService(err), {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            });
          });
      }
    },
    infiniteHandler($state) {
      axios(`https://jobshark.eu/api/v1/candidate/search`, {
        method: 'post',
        data: {
          page: this.currentPage,
          count: this.pageCount,
          employer_uuid: null,
          'opted-out': false,
        },
        headers: {
          Authorization: `Bearer ${this.getToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }).then(({ data }) => {
        const list = data.data;
        if (list.length) {
          this.currentPage += 1;
          this.infiniteScrollList.push(...list);
          $state.loaded();
        } else {
          $state.complete();
        }
      });
    },
    /*  getUpdatedHours(upDated) {
      if (upDated !== null) {
        return '';
      }

      const shortMonthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];

      const dateOne = new Date();
      const dateTwo = upDated;
      const dateOneObj = new Date(dateOne);
      const dateTwoObj = new Date(dateTwo);
      const milliseconds = Math.abs(dateTwoObj - dateOneObj);
      const hours = milliseconds / 36e5;

      if (hours <= 24) {
        return `${hours.toFixed()} hours ago`;
      }

      return `${dateTwoObj.getDay()}-${
        shortMonthNames[dateTwoObj.getMonth()]
      }-${dateTwoObj.getFullYear()}`;
    }, */
    nextPage() {
      if (this.currentPage >= 1) {
        this.isLoading = true;
        this.currentPage++;

        this.retrieveCandidates({
          search_word: this.searchWord,
          page: this.currentPage,
          count: this.pageCount,
          employersList: this.listOfSelectedEmployers.map(item => item.id),
          sourcesList: this.listOfSelectedSources.map(item => item.id),
          optedOut: this.isOptedOut,
          freelancers: this.freelancers,
          filters: this.selectedFiltersList,
        })
          .then(() => (this.isLoading = false))
          .catch(err => {
            this.isLoading = false;
            this.$toasted.error(errorService(err), {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            });
          });
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.isLoading = true;
        --this.currentPage;

        this.retrieveCandidates({
          search_word: this.searchWord,
          page: this.currentPage,
          count: this.pageCount,
          employersList: this.listOfSelectedEmployers.map(item => item.id),
          sourcesList: this.listOfSelectedSources.map(item => item.id),
          optedOut: this.isOptedOut,
          freelancers: this.freelancers,
          filters: this.selectedFiltersList,
        })
          .then(() => {
            this.isLoading = false;
          })
          .catch(err => {
            this.isLoading = false;
            this.$toasted.error(errorService(err), {
              theme: 'bubble',
              icon: 'fa-times-circle',
              position: 'bottom-right',
              duration: 8000,
            });
          });
      } else {
        this.currentPage = 1;
      }
    },
    setPageByNumber(pageNumber) {
      if (pageNumber > this.getTotalPages || pageNumber < 1) return;
      this.isLoading = true;
      this.currentPage = pageNumber;

      this.retrieveCandidates({
        search_word: this.searchWord,
        page: this.currentPage,
        count: this.pageCount,
        employersList: this.listOfSelectedEmployers.map(item => item.id),
        sourcesList: this.listOfSelectedSources.map(item => item.id),
        optedOut: this.isOptedOut,
        freelancers: this.freelancers,
        filters: this.selectedFiltersList,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;
          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    setToFirstPage() {
      this.isLoading = true;
      this.currentPage = 1;

      this.retrieveCandidates({
        search_word: this.searchWord,
        page: this.currentPage,
        count: this.pageCount,
        employersList: this.listOfSelectedEmployers.map(item => item.id),
        sourcesList: this.listOfSelectedSources.map(item => item.id),
        optedOut: this.isOptedOut,
        freelancers: this.freelancers,
        filters: this.selectedFiltersList,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.isLoading = false;
          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    setToLastPage(lastPage) {
      this.isLoading = true;
      this.currentPage = lastPage;

      this.retrieveCandidates({
        search_word: this.searchWord,
        page: this.currentPage,
        count: this.pageCount,
        employersList: this.listOfSelectedEmployers.map(item => item.id),
        sourcesList: this.listOfSelectedSources.map(item => item.id),
        optedOut: this.isOptedOut,
        freelancers: this.freelancers,
        filters: this.selectedFiltersList,
      })
        .then(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
          this.isLoading = false;
        });
    },
    getLocation(candidate) {
      if (candidate.city !== null)
        if (candidate.hasOwnProperty('city') && candidate.hasOwnProperty('country')) {
          if (candidate.city.hasOwnProperty('name') && candidate.country.hasOwnProperty('name')) {
            return `${candidate.city.name}, ${candidate.country.name}`;
          }
        }

      return null;
    },
    getCompetences(competences) {
      let slicedCompetences = competences;

      if (!slicedCompetences) {
        slicedCompetences = [];
      }

      slicedCompetences = competences.slice(0, 5);

      return slicedCompetences;
    },
    getRestCompetences(competences) {
      let restCompetences = competences;

      if (!restCompetences) {
        restCompetences = [];
      }

      restCompetences = competences.slice(5);

      return restCompetences;
    },
  },
  computed: {
    ...mapGetters([
      'getCompetencesFilterList',
      'getEmployersDropdown',
      'getLanguagesList',
      'getCitiesByName',
      'getAllCountries',
      'getCurrentPage',
      'getUserProfile',
      'getCurrentPage',
      'getCandidates',
      'getTotalPages',
      'getSourceList',
      'getToken',
    ]),
    candidateListMenus() {
      if (this.$route.path == '/admin/candidates') {
        return true;
      } else {
        return false;
      }
    },
  },
  watch: {
    listOfSelectedEmployers() {
      const parsed = JSON.stringify(this.listOfSelectedEmployers);
      localStorage.setItem('CandidateListOfSelectedEmployers', parsed);
    },
    currentPage() {
      localStorage.currentPageCandidateList = this.currentPage;
    },
  },

  created() {
    // Create a debounced version of the doTheSearch method
    this.debouncedSearch = debounce(this.doSearch, 800);
  },
  mounted() {
    this.displayResultsBasedOnScreen();
    this.retrieveCompetencesList();
    /*   if (localStorage.CandidateListOfSelectedEmployers)
      this.listOfSelectedEmployers = JSON.parse(localStorage.CandidateListOfSelectedEmployers);

    if (localStorage.currentPageCandidateList) {
      this.currentPage = Number(localStorage.currentPageCandidateList);
    } */

    this.isLoading = true;

    this.retrieveSourcesList();

    //Initial lodaing of candidates list
    this.retrieveCandidates({
      search_word: this.searchWord,
      page: this.currentPage,
      count: this.pageCount,
      optedOut: this.isOptedOut,
      freelancers: this.freelancers,
      employersList: this.listOfSelectedEmployers.map(item => item.id),
      sourcesList: this.listOfSelectedSources.map(item => item.id),
    })
      .then(() => {
        this.isLoading = false;
      })
      .catch(err => {
        this.isLoading = false;

        this.$toasted.error(errorService(err), {
          theme: 'bubble',
          icon: 'fa-times-circle',
          position: 'bottom-right',
          duration: 8000,
        });

        this.isLoading = false;
      });

    this.retrieveEmployersDropdown();
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.custom-label {
  line-height: 40px;
  position: absolute;
  right: 70px;
  top: 0;
  bottom: 0;
  -moz-transition: 0.3s right ease;
  -ms-transition: 0.3s right ease;
  -o-transition: 0.3s right ease;
  -webkit-transition: 0.3s right ease;
  transition: 0.3s right ease;
  z-index: 0;
}

.table-responsive {
  position: relative;
  thead {
    position: sticky;
    top: 0;
    z-index: 99;
  }
  tbody {
    border-top: none !important;
  }
  .table {
    &:not(:first-child) {
      border-top: 0px solid red;
    }
  }
}

#stage {
  .stage-size {
    width: 12px;
    height: 12px;
  }

  .stage-color-new {
    background-color: #4ca7d5;
  }
  .stage-color-draft {
    background-color: #8a8b8a;
  }
  .stage-color-active {
    background-color: #419941;
  }
  .stage-color-expired {
    background-color: #f17931;
  }
  .stage-color-rejected {
    background-color: #f02800;
  }
}

.candidate-list-card {
  height: 77vh;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
#candidate-employment {
  position: relative;
}

table {
  position: relative;
  min-width: 100% !important;
  tr {
    td {
      padding-bottom: 10.4px;
      padding-top: 10.5px;
    }
    th {
      width: 180px;
      &:first-of-type,
      &:nth-of-type(6),
      &:nth-of-type(8) {
        width: 240px;
      }
      &:nth-of-type(3) {
        width: 100px;
      }
      &:nth-of-type(4) {
        width: 120px;
      }
    }
  }

  .candidate-name {
    padding-left: 20px;
  }

  .collapse-competences {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 999;
    background: #fff;
    /*  max-width: 120%; */

    /*  .d-flex {
      padding: 8px 4px;
      flex-wrap: wrap;
    } */

    .no-icon {
      display: inline-block;
      background: #f2f2f2;
      border-radius: 8px;
      padding: 2px 5px;
      margin: 6px;
      font-size: 12px;
      color: #222;
    }
  }
  td {
    &:nth-of-type(2),
    &:nth-of-type(7),
    &:nth-of-type(8) {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.candidate-competences {
  position: relative;
  padding: 0 13px;
  z-index: 1;

  width: 100%;
  .tech-icons {
    width: 100%;
  }
  .icon-container {
    width: 44px;
    height: 44px;
    align-items: flex-end;
    z-index: 999;
  }
  .btn-danger {
    background: none !important;
    box-shadow: none !important;
    border: none !important;
    color: #222 !important;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    text-align: right;
    &:after {
      display: none;
    }
  }
  .dropdown-menu {
    max-width: 100%;
    min-width: 100%;
  }
  .list-inline {
    li {
      width: 50px;
      height: 50px;
      text-align: center;
      overflow: hidden;
    }
  }
  .dropdown {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.labeled {
  padding-top: 15px;

  span {
    font-size: 10px;
    display: inline-block;
    background: #fff;
    padding: 2px 5px;
    border-radius: 8px;
    margin-right: 0;
  }
}

tr {
  &:nth-child(2n) {
    .labeled {
      span {
        background: rgba(0, 0, 0, 0.05);
      }
    }
  }
}

.mobile-only {
  display: none !important;
}

.bottom-controls {
  position: absolute;
  top: 100%;
  left: -1px;
  background: #fff;
  width: 100.12%;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

@media only screen and(max-width: 576px) {
  .candidate-mobile {
    font-size: 15px;
    .email {
      color: #f05a00;
      white-space: nowrap;
      display: block;
      margin-bottom: 5px;
    }
    .location {
      color: #5f5f5f;
    }
    .recent-job {
      margin-bottom: 3px;
      span {
        display: block;
        color: #5f5f5f;
      }
    }
    .recent-company {
      color: #5f5f5f;
    }
  }
  .mobile-only {
    display: block !important;
  }
  .table-responsive,
  .bottom-controls {
  }
  .table-controls {
    display: flex;
    .controls-wrap {
      flex-wrap: wrap;
      width: 100%;
      .form-control {
        width: 100%;
        margin-right: 0 !important;
        margin: 0 0 12px 0;
        border-radius: 0;
      }
      #integration-search {
        border-radius: 0;
      }
      .btn-outline,
      .popperr,
      .btn-success {
        display: none;
      }
      .d-flex {
        width: 100%;
        .empl {
          &:nth-of-type(2) {
            margin-right: 0 !important;
            flex: 1;
          }
        }
      }
    }
  }
}

@media only screen and(min-width: 576px) and (max-width: 768px) {
  .table-controls {
    display: block;
    .controls-wrap {
      flex-wrap: wrap;
      width: 100%;
      .form-control {
        width: 100%;
        margin-right: 0 !important;
        margin: 0 0 12px 0;
        border-radius: 0;
      }
      #integration-search {
        border-radius: 0;
      }
      .btn-success,
      .popperr {
        display: none;
      }
      .d-flex {
        width: 100%;
        .empl {
          &:nth-of-type(2) {
            margin-right: 0 !important;
            flex: 1;
          }
        }
      }
    }
  }
}
@media only screen and(min-width: 768px) and (max-width: 992px) {
}
@media only screen and(min-width: 992px) and (max-width: px) {
}

/* @media only screen and (max-width: 500px) {
  .btn-success.mobile-only {
    display: none !important;
  }
  .controls-wrap {
    display: flex !important;
    flex-wrap: wrap;
    .form-control {
      margin: 15px 0 0 0;
    }
    .custom-checkbox.no-mobile {
      font-size: 14px;
      margin: 15px 0 0 0;
      display: none !important;
    }
    .custom-checkbox.mobile {
      font-size: 15px;
      margin: 14px 0 0 0;
    }
    .d-flex {
      width: 100%;
    }
    #integration-search {
      width: 85%;
      margin-bottom: 12px;
    }
    .btn-outline {
      align-self: center !important;
    }
    .popperr {
      display: none;
    }
    .btn-success.mobile-only {
      margin-right: 9px;
      margin-left: 0 !important;
    }
    .empl {
      &:first-of-type {
        margin-right: 15px;
      }
    }
  }
}

@media only screen and (min-width: 500px) and (max-width: 768px) {
  .card {
    border-top-left-radius: 18px !important;
    border-top-right-radius: 18px !important;
  }
  .table-controls {
    margin-bottom: 0 !important;
    .published {
      margin: 0;
    }
  }
  .integration-group {
    margin-right: 0 !important;
  }
  .custom-table {
    display: none;
  }
  .controls-wrap {
    display: flex !important;
    flex-wrap: wrap;
    .form-control {
      margin: 15px 0 0 0;
    }
    .custom-checkbox.no-mobile {
      font-size: 14px;
      margin: 15px 0 0 0;
      display: none !important;
    }
    .custom-checkbox.mobile {
      font-size: 15px;
      margin: 14px 0 0 0;
    }
    .d-flex {
      width: 100%;
    }
    #integration-search {
      width: 88.1%;
      margin-bottom: 12px;
    }
    .btn-outline {
      align-self: center !important;
    }
    .popperr {
      display: none;
    }
    .btn-success.mobile-only {
      margin-right: 9px;
      margin-left: 0 !important;
    }
    .empl {
      &:first-of-type {
        margin-right: 15px;
      }
    }
  }
}

@media only screen and (max-width: 1251px) {
  .card {
    margin-left: 23px !important;
    margin-right: 23px !important;
  }
}
@media only screen and (max-width: 992px) {
  .card {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .table-controls {
    margin-bottom: 0 !important;
    .published {
      margin: 0;
    }
  }
  .integration-group {
    margin-right: 0 !important;
  }
  .custom-table {
    display: none;
  }
  .controls-wrap {
    display: block;
    .form-control {
      margin: 15px 0 0 0;
      width: 100%;
    }
    .custom-checkbox.no-mobile {
      font-size: 14px;
      margin: 15px 0 0 0;
      display: none !important;
    }
    .custom-checkbox.mobile {
      font-size: 15px;
      margin: 14px 0 0 0;
    }
  }
  .desktop-only,
  .desktop-only-table {
    display: none;
  }
  .mobile-only {
    display: block !important;
  }
  .empl {
    width: 100%;
  }
}

@media only screen and (max-width: 768px) {
  .table-controls {
    margin-bottom: 0 !important;
    .published {
      margin: 0;
    }
  }
  .integration-group {
    margin-right: 0 !important;
  }
  .custom-table {
    display: none;
  }
  .controls-wrap {
    display: flex;
    .form-control {
      margin: 15px 0 0 0;
      width: 100%;
    }
    .custom-checkbox.no-mobile {
      font-size: 14px;
      margin: 15px 0 0 0;
      display: none !important;
    }
    .custom-checkbox.mobile {
      font-size: 15px;
      margin: 14px 0 0 0;
    }
  }
  .desktop-only,
  .desktop-only-table {
    display: none;
  }
} */

.vs__dropdown-toggle {
  min-height: 38px;
}
/* Filters */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* form-control style fix for custom v-select component */
/* /deep/ .filter-item .vs--searchable .vs__dropdown-toggle {
  height: calc(1.5em + 0.75rem + 2px);
} 
/* End Filters */

/* form-control style fix for custom v-select component */
.vs--searchable .vs__dropdown-toggle {
  height: calc(1.5em + 0.75rem + 2px);
}

.col-md-8.filter {
  color: #6c757d;
  margin-bottom: 12px;
  font-family: $font-family-sans-lato;
}

.filter-select {
  min-width: 200px;
  max-width: 200px;
  margin-right: 6px !important;
  padding-bottom: 8px;
}
</style>
